import { useGPFormContext } from '@/contexts/GPFormContext';
import {
  Box,
  Divider,
  FormControlLabel,
  IconButton,
  keyframes,
  List,
  ListItem,
  Switch,
  Typography,
} from '@mui/material';
import { ArrowLeft2 } from 'givepulse-iconsax-react';
import { ElementRef, Fragment, RefObject, useEffect, useMemo, useRef, useState } from 'react';
import { get, set } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { ArrayUID, ArrayWithUID } from '../requests/types';
import { shouldShowFilterOptionsSearch } from '@/utils/filterOptionsSearch';
import { isFilterFieldActive } from '@/utils/filterFieldActive';
import { isToggleFilterSection } from '@/utils/filterToggleSection';
import { useFilterToggleSection } from '@/hooks/useFilterToggleSection';
import GPFilterReverseButton from './GPFilterReverseButton';
import { CHECKBOX_GROUP_TYPE } from '@/types/forms/fields';
import { FormSectionType } from '../types/forms';
import GPButton from './GPButton';
import GPFieldServer from './GPFieldServer';
import GPMobileDrawerBottom from './GPMobileDrawerBottom';
import GPStackScreen from './GPStackScreen';

// Highlight animation for filter button when updated via NLP
const filterHighlight = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(25, 118, 210, 0.4);
  }
  50% {
    box-shadow: 0 0 0 4px rgba(25, 118, 210, 0.2);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(25, 118, 210, 0);
  }
`;

const useStyles = makeStyles()(theme => ({
  mainContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: theme.palette.background.default,
    backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.15), rgba(255, 255, 255, 0.15))',
  },

  button: {
    backgroundColor: theme.palette.background.default,
  },
  
  highlightAnimation: {
    animation: `${filterHighlight} 1.5s ease-out`,
  },

  filterList: {
    minHeight: 0,
    overflowY: 'auto',
    flexGrow: 1,
  },

  stackBackButton: {
    paddingLeft: 0,
    // marginLeft: '-20px',
  },

  fieldsContainer: {
    minHeight: 0,
    flexGrow: 1,
    overflowY: 'auto',
  },

  fieldsContainerFill: {
    minHeight: 0,
    flexGrow: 1,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },

  formControlFill: {
    marginTop: '16px',
    width: '100%',
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',

    '& .MuiFormControlLabel-label': {
      fontSize: theme.typography.h6.fontSize,
    },
  },

  filterItem: {
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
  },

  toggleControl: {
    margin: 0,
    width: '100%',
    justifyContent: 'space-between',
  },

  formControl: {
    marginTop: '16px',
    width: '100%',

    '& .MuiFormControlLabel-label': {
      fontSize: theme.typography.h6.fontSize,
    },
  },
}));

const FilterScreen = ({
  button,
  stackRef,
}: {
  button: FormSectionType & ArrayUID;
  stackRef: RefObject<ElementRef<typeof GPStackScreen>>;
}) => {
  const { reset, defaultValues, getValues, handleSubmit, onSubmit, valueState } =
    useGPFormContext();

  const fieldNames = useMemo(() => button.fields.map(field => field.name), [button.fields]);

  const usesInternalOptionScroll =
    button.fields.length === 1 &&
    button.fields[0].type === CHECKBOX_GROUP_TYPE &&
    shouldShowFilterOptionsSearch(button.fields[0].name);

  const { classes } = useStyles();
  const { t } = useTranslation();

  // Leaving the filter screen without Apply is a cancel: revert this section's fields to
  // the last applied state so pending, unsubmitted edits are discarded.
  const handleBack = () => {
    const newValues = { ...getValues() };
    fieldNames.forEach(name => {
      if (name) set(newValues, name, get(valueState, name));
    });
    reset(newValues);
    if (stackRef.current) {
      stackRef.current.prevScreen();
    }
  };

  const handleApply = () => {
    handleSubmit(onSubmit)();
  };

  const handleReset = () => {
    const newValues = { ...getValues() };
    fieldNames.forEach(name => {
      name && set(newValues, name, get(defaultValues, name));
    });
    reset(newValues);
    handleSubmit(onSubmit)();
  };

  return (
    <Box className={classes.mainContainer}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexShrink: 0
        }}>
        <IconButton className={classes.stackBackButton} size="small" onClick={handleBack}>
          <ArrowLeft2 size={20} />
        </IconButton>
        <Typography variant="h4">{button.label}</Typography>
        {button.reversible && (
          <Box sx={{ ml: 'auto' }}>
            <GPFilterReverseButton button={button} />
          </Box>
        )}
      </Box>
      <Box
        className={`${
          usesInternalOptionScroll ? classes.fieldsContainerFill : classes.fieldsContainer
        } GPAnchorScrollBar`}
      >
        {button.fields.map(field => (
          <Box
            className={usesInternalOptionScroll ? classes.formControlFill : classes.formControl}
            key={field.unique_id}
            sx={{
              mb: usesInternalOptionScroll ? 0 : 4
            }}
          >
            <GPFieldServer field={field} label={button.label} isFilter showSearch={shouldShowFilterOptionsSearch(field.name)} />
          </Box>
        ))}
      </Box>
      <Box sx={{
        mt: 1
      }}>
        <Box sx={{
          mb: 1
        }}>
          <GPButton
            round
            fullWidth
            type="submit"
            variant="contained"
            color="primary"
            onClick={handleApply}
          >
            {t('Apply')}
          </GPButton>
        </Box>
        <GPButton round fullWidth onClick={handleReset}>
          {t('Clear')}
        </GPButton>
      </Box>
    </Box>
  );
};

/**
 * A filter with nothing to choose (Action Required) is a plain on/off, so it stays on
 * the filter list as an inline switch rather than pushing a screen with an Apply button.
 */
const GPFilterRowMobileToggleItem = ({ button }: { button: FormSectionType }) => {
  const { classes } = useStyles();
  const { active, toggle } = useFilterToggleSection(button);

  return (
    <ListItem disableGutters className={classes.filterItem}>
      <FormControlLabel
        className={classes.toggleControl}
        control={<Switch color="primary" checked={active} onChange={toggle} />}
        label={<Typography variant="h6">{button.label}</Typography>}
        labelPlacement="start"
      />
    </ListItem>
  );
};

type GPFilterRowItemProps = {
  button: FormSectionType;
  stackRef: RefObject<ElementRef<typeof GPStackScreen>>;
};

const GPFilterRowMobileItem = ({ button, stackRef }: GPFilterRowItemProps) => {
  const { valueState, defaultValues, setModified } = useGPFormContext();
  const [localFiltered, setLocalFiltered] = useState(new Set<string>());

  const fieldNames = useMemo(() => button.fields.map(field => field.name), [button.fields]);

  useEffect(() => {
    const nextLocal = new Set<string>();

    fieldNames.forEach(name => {
      if (name && isFilterFieldActive(valueState, name, defaultValues)) {
        nextLocal.add(name);
      }
    });

    setLocalFiltered(nextLocal);

    setModified(prev => {
      const next = new Set(prev);
      fieldNames.forEach(name => {
        if (!name) return;
        if (nextLocal.has(name)) {
          next.add(name);
        } else {
          next.delete(name);
        }
      });
      return next;
    });
  }, [valueState, defaultValues, fieldNames, setModified]);

  const { classes } = useStyles();

  const handleNextScreen = (_button: FormSectionType & ArrayUID) => () => {
    if (stackRef.current) {
      stackRef.current.nextScreen(<FilterScreen stackRef={stackRef} button={_button} />);
    }
  };

  return (
    <ListItem
      disableGutters
      className={classes.filterItem}
      key={`${button.label_raw}-${button.unique_id}`}
      onClick={handleNextScreen(button)}
    >
      <Typography variant="h6">
        {`${button.label}${localFiltered.size > 0 ? ` (${localFiltered.size})` : ''}`}
      </Typography>
    </ListItem>
  );
};

type GPFilterRowMobileProps = {
  filterButtons: ArrayWithUID<FormSectionType>;
  /** When true, applies highlight animation to filter button */
  highlight?: boolean;
};

const GPFilterRowMobile = ({ filterButtons, highlight }: GPFilterRowMobileProps) => {
  const { reset, handleSubmit, onSubmit, defaultValues, valueState, modified } = useGPFormContext();

  const [open, setOpen] = useState(false);

  const stackRef = useRef<ElementRef<typeof GPStackScreen>>(null);

  const handleClearAll = () => {
    reset(defaultValues);
    handleSubmit(onSubmit)();
  };

  useEffect(() => {
    setOpen(false);
  }, [valueState]);

  const { t } = useTranslation();
  const { classes } = useStyles();
  return (
    <Box>
      <GPButton
        size="small"
        variant="outlined"
        onClick={() => {
          setOpen(true);
        }}
        className={highlight ? `${classes.button} ${classes.highlightAnimation}` : classes.button}
      >
        {`${t('Filter', { count: 2 })}${modified.size > 0 ? ` (${modified.size})` : ''}`}
      </GPButton>
      <GPMobileDrawerBottom
        open={open}
        handleOpen={() => {
          setOpen(true);
        }}
        handleClose={() => {
          setOpen(false);
        }}
      >
        <Box
          sx={{
            height: "60vh",
            px: 3,
            py: 2
          }}>
          <GPStackScreen ref={stackRef}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                height: "100%"
              }}>
              <Box
                sx={{
                  minHeight: 48,
                  display: "flex",
                  alignItems: "center",
                  flexShrink: 0
                }}>
                <Typography variant="h4">{t('Filter', { count: filterButtons.length })}</Typography>
              </Box>
              {filterButtons.length > 0 && (
                <>
                  <List disablePadding className={`${classes.filterList} GPAnchorScrollBar`}>
                    {filterButtons.map(button => {
                      if (button.hide) return null;

                      return (
                        <Fragment key={`${button.label_raw}-${button.unique_id}`}>
                          {isToggleFilterSection(button) ? (
                            <GPFilterRowMobileToggleItem button={button} />
                          ) : (
                            <GPFilterRowMobileItem stackRef={stackRef} button={button} />
                          )}
                          <Divider />
                        </Fragment>
                      );
                    })}
                  </List>
                  <Box
                    sx={{
                      mt: 1,
                      display: "flex",
                      justifyContent: "flex-end"
                    }}>
                    <GPButton onClick={handleClearAll}>{t('Clear all')}</GPButton>
                  </Box>
                </>
              )}
            </Box>
          </GPStackScreen>
        </Box>
      </GPMobileDrawerBottom>
    </Box>
  );
};

export default GPFilterRowMobile;
